import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import DropinElement from '../../node_modules/@adyen/adyen-web/dist/types/components/Dropin/Dropin';
import { CoreOptions } from '../../node_modules/@adyen/adyen-web/dist/types/core/types';
import { stringifyAllForDebugging } from '@daymade/common-lib/types';
import { getAdyenPaymentDetails } from 'api/payments';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useRouter } from 'next/router';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { trackEvent } from 'utils/analytics/analytics';
import { adyenApiClient } from 'utils/apiClient';
import { sleep } from 'utils/common';
import { runningInProduction } from 'utils/constants';
import { getCookie } from 'utils/cookie';
import HttpStatusCode from 'utils/HttpStatusCode';
import { isFacebookApp } from 'utils/ua';
import { adyenClientKey } from '../../private/adyen';

interface IAdyenDropInCheckout {
  queryParamsObject: any;
  paymentSession: IPaymentSession;
  initialRedirectResult?: string | null;
  displayError: (error: string | null) => Promise<void>;
  completionHandler: () => void;
  onLoadingStateChange: (loading: boolean) => void;
  style?: CSSProperties;
}

interface IPaymentSession {
  id: string;
  sessionData: string;
  adyenPaymentSessionId: string;
}

// `redirectResult` is an optional parameter that is only supplied when this component is displayed on the /validate-3ds-payment page.
const AdyenDropInCheckout = ({
  queryParamsObject /* used by validate-3ds-payment page */,
  paymentSession,
  initialRedirectResult,
  displayError,
  completionHandler = () => {},
  onLoadingStateChange,
  style = {},
}: IAdyenDropInCheckout) => {
  const router = useRouter();

  const dropInContainer = useRef<HTMLDivElement | null>(null);
  const adyenComponent = useRef<DropinElement>();
  const { outsideUK } = useUserStore();

  const [loadingForm, setLoadingForm] = useState(true);

  const [redirectResult, setRedirectResult] = useState<string | null>(
    initialRedirectResult ?? null,
  );

  const latestRefusalReason = useRef<string>();

  const refusalReasonTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const useRedirect = false;

  const handleSuccessfulPayment = () => {
    displayError(null);

    // Show loading spinner while we wait for the AUTHORISATION webhook to hit the API.
    onLoadingStateChange(true);

    // This does not advance to confirmation page, but it does fire off some GA EEC tracking events.
    completionHandler();
  };

  const handleUnsuccessfulPayment = async (optionalRefusualReason?: string) => {
    const resp = await adyenApiClient.auth.getAdyenPaymentRefusalReason({
      query: { adyenPaymentSessionId: paymentSession.adyenPaymentSessionId },
    });

    let refusalReason =
      optionalRefusualReason ||
      (resp.status === HttpStatusCode.OK ? resp.body.reason : undefined);
    console.log('refusalReason', refusalReason);

    if (refusalReason?.toUpperCase().includes('MCC')) {
      refusalReason = `${refusalReason}: this is likely due to you using a credit (rather than debit) card`;
    }

    // Clear timeout in case there is one already running (e.g. after someone has already had a failed payment).
    if (refusalReasonTimerRef.current) {
      clearTimeout(refusalReasonTimerRef.current);
    }

    if (refusalReason) {
      if (refusalReason !== latestRefusalReason.current) {
        latestRefusalReason.current = refusalReason;
        displayErrorWithOptionalRefusualReason(refusalReason);
      }
      // Check again in 2 second in case an updated webhook comes through e.g. for a subsequent failed payment.
      refusalReasonTimerRef.current = setTimeout(
        handleUnsuccessfulPayment,
        2000,
      );
    } else {
      refusalReason = 'GENERIC';

      if (refusalReason !== latestRefusalReason.current) {
        latestRefusalReason.current = 'GENERIC';
        displayErrorWithOptionalRefusualReason();
      }
      // Try again in 1 second (maybe webhook will have come through by then).
      refusalReasonTimerRef.current = setTimeout(
        handleUnsuccessfulPayment,
        1000,
      );
    }
  };

  const displayErrorWithOptionalRefusualReason = (refusalReason?: string) => {
    if (refusalReason) {
      displayError(
        `Your payment didn't go through (${refusalReason}). Please note that we only accept UK debit MasterCard and Visa cards. Please try again, or drop us an email if you need any help: hello@daymade.co.uk.`,
      );
    } else {
      displayError(
        "Your payment didn't go through. Please note that we only accept UK debit MasterCard and Visa cards. Please try again, or drop us an email if you need any help: hello@daymade.co.uk.",
      );
    }
  };

  const initialiseCheckout = async () => {
    if (outsideUK || !router.isReady) {
      return;
    }
    const configuration: CoreOptions = {
      environment: runningInProduction ? 'live' : 'test',
      session: {
        id: paymentSession.id,
        sessionData: paymentSession.sessionData,
      },
      clientKey: runningInProduction
        ? adyenClientKey.live
        : adyenClientKey.test,
      ...(useRedirect
        ? {
            onSubmit: async (state, component) => {
              displayError(null);
              component.setStatus('loading');
              const queryParamString = Object.keys(queryParamsObject)
                .map((key) => `${key}=${queryParamsObject[key]}`)
                .join('&');

              const response =
                await adyenApiClient.auth.makeAdyenPaymentRequest({
                  body: {
                    adyenPaymentSessionId: paymentSession.adyenPaymentSessionId,
                    sessionId: paymentSession.id,
                    adyenState: state,
                    queryParams: queryParamString,
                  },
                });
              component.setStatus('ready');
              onLoadingStateChange(false);
              // Handle both possible response types: success (status 200) and error (e.g., status 401)
              if (response.status === HttpStatusCode.OK) {
                const { resultCode, refusalReason, action } = response.body;
                if (action) {
                  component.handleAction(action);
                } else if (resultCode === 'Authorised') {
                  console.log('Successful payment ✅ ');
                  return handleSuccessfulPayment();
                } else if (resultCode === 'Refused') {
                  console.error('Unsuccessful payment ❌');
                  await handleUnsuccessfulPayment(refusalReason);

                  trackEvent('adyen_payment_failed', {
                    reason: refusalReason,
                    id: paymentSession.id,
                  });

                  // Clear this so the form can render properly to take payment again
                  setRedirectResult(null);

                  // Wait for the state update
                  await sleep(200);

                  // Display payment form again
                  if (!redirectResult) {
                    component.unmount();
                    initialiseCheckout();
                  }
                }
              } else {
                // Handle error (e.g., unauthorized)
                displayError(
                  'There was a problem processing your payment. Please try again.',
                );
                setRedirectResult(null);
                await sleep(200);
                if (!redirectResult) {
                  component.unmount();
                  initialiseCheckout();
                }
              }
            },
            onAdditionalDetails: async (state, component) => {
              console.log('onAdditionalDetails', state);
              const result = await getAdyenPaymentDetails(
                getCookie('jwt'),
                state,
              );
              if (result.resultCode === 'Refused') {
                console.error('Unsuccessful payment ❌');
                await handleUnsuccessfulPayment(result.refusalReason);

                trackEvent('adyen_payment_failed', {
                  reason: result.refusalReason,
                  id: paymentSession.id,
                });

                // Clear this so the form can render properly to take payment again
                setRedirectResult(null);

                // Wait for the state update
                await sleep(200);

                // Display payment form again
                if (!redirectResult) {
                  component?.unmount();
                  initialiseCheckout();
                }
              }
            },
          }
        : undefined),

      onPaymentCompleted: async (result, component) => {
        console.log('onPaymentCompleted', result, JSON.stringify(result));

        displayError(null);

        switch (result.resultCode) {
          case 'Authorised':
            console.log('Successful payment ✅ ');
            return handleSuccessfulPayment();

          case 'Refused':
          default: {
            console.error('Unsuccessful payment ❌');
            await handleUnsuccessfulPayment();

            trackEvent('adyen_payment_failed', {
              id: paymentSession.id,
              reason: result.resultCode,
            });

            // Clear this so the form can render properly to take payment again
            setRedirectResult(null);

            // Wait for the state update
            await sleep(200);

            // Display payment form again
            if (!redirectResult) {
              component?.unmount();
              initialiseCheckout();
            }
          }
        }
      },

      onError: (error) => {
        console.log(
          'Error caught by AdyenDropInCheckout',
          error?.constructor.name,
          stringifyAllForDebugging(error),
        );

        if (error.name !== 'CANCEL') {
          displayErrorWithOptionalRefusualReason();
        }

        trackEvent('adyen_payment_error', {
          id: paymentSession.id,
          reason: error.name,
        });
      },
      paymentMethodsConfiguration: {
        card: {
          name: 'Debit Card',
        },
        applepay: {
          buttonType: 'plain',
          onValidateMerchant: (resolve, reject, validationUrl) => {
            // Your server uses the validation URL to request a session from the Apple Pay server.
            // Call resolve(MERCHANTSESSION) or reject() to complete merchant validation.
            adyenApiClient.auth
              .createApplePayMerchantSession({ body: { validationUrl } })
              .then((response) => {
                // Complete merchant validation with resolve(MERCHANTSESSION) after receiving an opaque merchant session object, MerchantSession
                return resolve(response.body);
              })
              .catch((error) => {
                // Complete merchant validation with reject() if any error occurs
                reject();
                throw error;
              });
          },
        },
        paywithgoogle: {
          configuration: {
            merchantName: 'DAYMADE',
            gatewayMerchantId: 'TripHuntersCOM',
            merchantId: 'BCR2DN6TQ6ON3QJK',
          },
          allowCreditCards: false,
        },
        paypal: {
          blockPayPalCreditButton: true,
          blockPayPalPayLaterButton: true,
        },
      },
    };

    const checkout = await AdyenCheckout(configuration);

    // Filter out saved Apply Pay payment methods as these cannot be used in the drop-in payment form.
    checkout.paymentMethodsResponse.storedPaymentMethods =
      checkout.paymentMethodsResponse.storedPaymentMethods.filter(
        (storedPaymentMethod) =>
          !storedPaymentMethod.brand?.includes('applepay'),
      );

    const isFb = isFacebookApp(isMobile);

    if (isFb) {
      checkout.paymentMethodsResponse.paymentMethods =
        checkout.paymentMethodsResponse.paymentMethods.filter(
          (method) => method.type !== 'paywithgoogle',
        );
    }

    if (redirectResult) {
      checkout.submitDetails({ details: { redirectResult } });
    } else {
      if (dropInContainer.current) {
        adyenComponent.current = checkout
          .create('dropin', {
            instantPaymentTypes: [],
          })
          .mount(dropInContainer.current);
      }
    }
    setLoadingForm(false);
  };

  useEffect(() => {
    (async () => {
      await initialiseCheckout();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectResult, paymentSession, router.isReady, isMobile]);

  useEffect(() => {
    return () => {
      if (refusalReasonTimerRef.current) {
        clearTimeout(refusalReasonTimerRef.current);
      }

      if (adyenComponent.current) {
        adyenComponent.current.unmount();
      }
    };
  }, []);
  if (outsideUK) {
    return (
      <div className="w-full h-full flex flex-col justify-center">
        <h3
          style={{
            fontWeight: 800,
            letterSpacing: 'normal',
            textShadow: '2px 2px white',
          }}
        >
          It looks like you are visiting us from outside the United Kingdom
        </h3>
        <p>
          Please note that DAYMADE operates exclusively in the UK and is
          available to UK residents only. We hope to bring the DAYMADE
          experience to your country one day!
        </p>
      </div>
    );
  }
  return (
    <div style={style}>
      {loadingForm && <PlainLoadingSpinner style={{ margin: '2em auto' }} />}

      <div ref={dropInContainer} />

      <p
        className="adyenDropInCheckoutCreditCardsMessage"
        style={{
          fontWeight: 500,
          fontStyle: 'italic',
          color: '#777',
          margin: '1em auto',
          textAlign: 'center',
        }}
      >
        (Note: Credit Cards not accepted)
      </p>

      <style jsx>
        {`
          :global(.adyen-checkout__applepay__button) {
            -webkit-appearance: -apple-pay-button !important;
          }

          :global(.adyen-checkout__payment-method__header__title:hover) {
            background: unset;
          }

          :global(.adyen-checkout__button:hover .adyen-checkout__button__text) {
            color: #ccc;
          }

          :global(.adyen-checkout__error-text) {
            font-size: 1em;
          }

          :global(.adyen-checkout__button--pay) {
            font-weight: bold;
          }
        `}
      </style>
    </div>
  );
};

export default AdyenDropInCheckout;
